"use client";

import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MobileTabChips, useSwipeTabs, type MobileTab } from "@/components/ui/mobile-tab-chips";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { useGetUserQuery } from "@/api/auth/authPrivateApi";
import { EXTERNAL_STUDENT_LABEL_SCHOOL_ID } from "@/app/assignments/[id]/components/DataTable/columns";
import { PublicStudentHeader } from "./PublicStudentHeader";
import PublicResultsTab from "./tabs/PublicResultsTab";
import PublicControlTab from "./tabs/PublicControlTab";
import PublicLogsTab from "./tabs/PublicLogsTab";
import PublicReportTab from "./tabs/PublicReportTab";
import type {
  ReportStudent,
  ReportAssignment,
  ReportAttemptBasic,
  ReportCertificate,
} from "@/types/endpoints/reports/GetAssignmentReportInfo";
import type { AssignmentReportDataResponse, ReportWebinarSession } from "@/types/endpoints/reports/GetAssignmentReportData";
import type { GetAssignmentReportResultsResponse } from "@/types/endpoints/reports/GetAssignmentReportResults";
import type { WebinarRecord } from "@/types/endpoints/assignments/GetWebinarSessions";

interface PublicStudentContentProps {
  /** Student data from reportInfo */
  student: ReportStudent;
  /** Assignment data from reportInfo */
  assignment: ReportAssignment;
  /** Certificate data from reportInfo */
  certificate?: ReportCertificate | null;
  /** Detailed report data from reportData */
  reportData?: AssignmentReportDataResponse;
  /** Results list fetched from /results.json */
  resultsData?: GetAssignmentReportResultsResponse;
  /** List of attempts for the student */
  attempts: ReportAttemptBasic[];
  /** Currently selected attempt ID */
  selectedAttemptId: number;
  /** Callback when attempt changes */
  onAttemptChange: (attemptId: number) => void;
}

/**
 * PublicStudentContent - main container for student data on public report page
 * Displays header with student info and tabs for Report, Results, Control, Logs
 */
export function PublicStudentContent({
  student,
  assignment,
  certificate,
  reportData,
  resultsData,
  attempts,
  selectedAttemptId,
  onAttemptChange,
}: PublicStudentContentProps) {
  const t = useTranslations();

  const isExternalAssignment = assignment.type === "external";

  const { data: currentUser } = useGetUserQuery();
  const currentSchoolId =
    (currentUser as { school_id?: number | null } | undefined)?.school_id ?? null;
  const showRichLabel = currentSchoolId === EXTERNAL_STUDENT_LABEL_SCHOOL_ID;

  const fio = student.user
    ? [student.user.firstname, student.user.lastname, student.user.patronymic]
        .filter(Boolean)
        .join(" ")
    : "";
  const externalRaw =
    student.user?.external_id ??
    (student as { external_id?: number | string | null }).external_id ??
    null;
  const externalId =
    externalRaw == null || String(externalRaw).trim() === ""
      ? ""
      : String(externalRaw).trim();
  const email = (student.user?.email ?? student.email ?? "").trim();
  const fallbackName = student.name || `Student #${student.id}`;
  const fullName = showRichLabel
    ? [fio, externalId, email].filter(Boolean).join(" · ") || fallbackName
    : fio || fallbackName;

  // Get photo (try multiple fields)
  const photo = student.user?.photo || student.user?.photo_thumb || student.user?.avatar;

  const isMobile = useIsMobile();

  const selectedAttempt = reportData?.entity?.attempts?.find(
    (attempt) => attempt.id === selectedAttemptId
  );

  const isReportAvailable = reportData?.entity?.is_started === true;
  const reportFallbackTab = "control";

  const [currentTab, setCurrentTab] = React.useState<string>(
    isReportAvailable ? "report" : reportFallbackTab
  );

  React.useEffect(() => {
    if (currentTab === "report" && !isReportAvailable) {
      setCurrentTab(reportFallbackTab);
    }
  }, [currentTab, isReportAvailable]);

  const visibleTabs: MobileTab[] = [
    isReportAvailable && { value: "report", label: t("tab-ai-report") },
    !isExternalAssignment && {
      value: "results",
      label: t("label-assignment-results"),
    },
    { value: "control", label: t("tab-control") },
  ].filter(Boolean) as MobileTab[];

  const swipeHandlers = useSwipeTabs(
    visibleTabs,
    currentTab,
    setCurrentTab,
    isMobile
  );

  // Find current attempt index for label
  const currentAttemptIndex = attempts.findIndex((a) => a.id === selectedAttemptId);
  const currentAttemptNumber = currentAttemptIndex >= 0 ? currentAttemptIndex + 1 : 1;
  const currentAttemptLabel = t("label-attempt-number", { number: currentAttemptNumber });

  // Merge webinar_sessions with webinar_records by webinar_session_id
  const videoSessionsWithRecords = useMemo((): ReportWebinarSession[] => {
    const sessions = reportData?.entity?.webinar_sessions || [];
    const records = reportData?.entity?.webinar_records || [];
    
    if (records.length === 0) {
      return sessions;
    }
    
    return sessions.map((session) => {
      const record = records.find((r) => r.webinar_session_id === session.id);
      return {
        ...session,
        record: record || session.record,
      };
    });
  }, [reportData?.entity?.webinar_sessions, reportData?.entity?.webinar_records]);

  return (
    <div className="flex flex-col gap-4">
      {/* Student Header */}
      <PublicStudentHeader
        fullName={fullName}
        nameForInitials={fio || fallbackName}
        photo={photo}
        startedAt={selectedAttempt?.started_at ?? null}
        finishedAt={selectedAttempt?.finished_at ?? null}
        score={student.points ?? null}
        isExternal={isExternalAssignment}
      />

      {/* Attempt Selector (if multiple attempts) */}
      {attempts.length > 1 && (
        <div className="flex items-center justify-between px-1">
          <Select
            value={String(selectedAttemptId)}
            onValueChange={(val) => {
              const idNum = Number(val);
              if (!Number.isNaN(idNum)) {
                onAttemptChange(idNum);
              }
            }}
          >
            <SelectTrigger className="inline-flex items-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-3 py-1.5 text-sm h-auto min-h-0 w-auto">
              <SelectValue>{currentAttemptLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent className="z-[120]">
              {attempts.map((at, index) => (
                <SelectItem key={at.id} value={String(at.id)}>
                  {t("label-attempt-number", { number: index + 1 })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-xs text-muted-foreground">
            {t("label-attempts-total", { count: attempts.length })}
          </span>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        {isMobile ? (
          <MobileTabChips
            tabs={visibleTabs}
            value={currentTab}
            onValueChange={setCurrentTab}
          />
        ) : (
          <TabsList className="w-full justify-start">
            {isReportAvailable && (
              <TabsTrigger value="report">{t("tab-ai-report")}</TabsTrigger>
            )}
            {!isExternalAssignment && (
              <TabsTrigger value="results">{t("label-assignment-results")}</TabsTrigger>
            )}
            <TabsTrigger value="control">{t("tab-control")}</TabsTrigger>
            {/* <TabsTrigger value="logs">{t("label-assignment-actions")}</TabsTrigger> */}
          </TabsList>
        )}

        <div className="contents" {...swipeHandlers}>
        {isReportAvailable && (
          <TabsContent
            value="report"
            forceMount
            className="data-[state=inactive]:hidden"
          >
            <PublicReportTab
              attemptId={selectedAttemptId}
              credibility={selectedAttempt?.credibility}
              webinarRecords={reportData?.entity?.webinar_records as WebinarRecord[] | undefined}
              resultsChart={reportData?.entity?.results_chart as Array<{ name: string; data: number[] }> | undefined}
              assignmentType={assignment.type}
              credibilityVerdict={reportData?.entity?.credibility_verdict}
            />
          </TabsContent>
        )}

        {!isExternalAssignment && (
          <TabsContent value="results">
            <PublicResultsTab
              quizComponents={reportData?.entity?.quiz_components}
              results={reportData?.entity?.results}
              resultsData={resultsData}
              studentAssessments={reportData?.entity?.student_assessments}
              totalPoints={student.points}
              isPointSystemEnabled={assignment.is_point_system_enabled}
              assessmentName={assignment.name || assignment.title}
              certificate={certificate}
            />
          </TabsContent>
        )}

        <TabsContent value="control">
          <PublicControlTab
            identities={reportData?.entity?.identities || []}
            videoSessions={videoSessionsWithRecords}
            violations={reportData?.entity?.violations?.data || []}
          />
        </TabsContent>

        {/* <TabsContent value="logs">
          <PublicLogsTab actions={reportData?.entity?.actions?.data || []} />
        </TabsContent> */}
        </div>
      </Tabs>
    </div>
  );
}

export default PublicStudentContent;
